import {Box, getValueByDevice, useDevice} from 'platform/foundation';
import {currencies, formatCurrencySymbol, useFormatNumber} from 'platform/locale';
import {
  CartesianGrid,
  Label,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  YAxisProps,
} from 'recharts';
import {createGlobalStyle, useTheme} from 'styled-components';

import {useMemo} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';

import {length} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {PriceMapCarResponseBody, SourcingVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {CurrencyCodeType, suffixTestId, TestIdProps} from 'shared/web';

import {useSourcingCurrency} from '../../../../../../hooks/useSourcingCurrency';
import {ChartTooltip} from './components/ChartTooltip';
import {ScatterShape} from './components/ScatterShape';
import {renderReferenceLineX} from './renders/renderReferenceLineX';
import {renderReferenceLineY} from './renders/renderReferenceLineY';
import {PriceMapChartItem} from './types/PriceMapChartItem';
import {addMyVehicleToAllCars} from './utils/addMyVehicleToAllCars';
import {getAverageValueWithoutMyVehicle} from './utils/getAverageValueWithoutMyVehicle';

const AXIS_OFFSET = 100;
const ROUND_TO_VALUE = 100;
const MY_VEHICLE_COLOR = '#0A2442';

const axisDomain: YAxisProps['domain'] = [
  (minValue: number) =>
    Math.max(0, Math.floor((minValue - AXIS_OFFSET) / ROUND_TO_VALUE) * ROUND_TO_VALUE),
  'auto',
];

// Global styles for chart SVG elements
// eslint-disable-next-line eag/no-css-property
const ChartGlobalStyles = createGlobalStyle`
  .recharts-surface {
    overflow: visible !important;
  }
`;

interface PriceMapProps extends TestIdProps {
  cars: PriceMapCarResponseBody[];
  queryPriceIndex: number;
  vehicle: SourcingVehicleDetailResponseBody;
  selectedVehicleId: string | null;
  setSelectedVehicleId: (id: string | null) => void;
}

export function PriceMap(props: PriceMapProps) {
  const theme = useTheme();
  const device = useDevice();
  const formatNumber = useFormatNumber();
  const currencyCode = (useSourcingCurrency()?.code ?? 'CZK') as CurrencyCodeType;

  const carsWithMyVehicle = useMemo(
    () => addMyVehicleToAllCars(props.cars, props.vehicle.sourcingVehicle, props.queryPriceIndex),
    [props.cars, props.vehicle.sourcingVehicle, props.queryPriceIndex]
  );

  // mapped data must be memoized - when mapped data is changed, recharts reset current zoom to default
  const mappedData = useMemo(
    () =>
      carsWithMyVehicle.map((car, index) => {
        const coordinates = {x: car.mileage ?? 0, y: car.price_with_vat ?? 0};

        return {
          index,
          car,
          ...coordinates,
        };
      }),
    [carsWithMyVehicle]
  );

  const averageValueX = getAverageValueWithoutMyVehicle('x', mappedData);
  const averageValueY = getAverageValueWithoutMyVehicle('y', mappedData);

  const myVehicleItem = mappedData.find((item) => item.car.isMyVehicle);

  const xAxisTickFormatter = (value: number) =>
    `${formatNumber(value)} ${i18n.t('general.metric.km')}`;
  const yAxisTickFormatter = (value: number) => formatNumber(Math.round(value / 1000)) ?? '';

  const yReferenceLineLabelFormatter = (value: number) =>
    i18n.t('entity.priceMap.labels.thousandsCurrency', {
      value: yAxisTickFormatter(value),
      currencySymbol: formatCurrencySymbol(currencies, currencyCode),
    });

  const onClick = (item: PriceMapChartItem) => {
    props.setSelectedVehicleId(props.selectedVehicleId === item.car.ad_id ? null : item.car.ad_id);
  };

  return (
    <Box height="100%">
      <ChartGlobalStyles />
      <AutoSizer>
        {({width, height}: {width: number; height: number}) => (
          <ScatterChart
            data={mappedData}
            width={width}
            height={height}
            margin={{top: 0, right: 0, bottom: 0, left: 0}}
          >
            <CartesianGrid stroke={theme.colors.palettes.neutral['30'][100]} />
            <Scatter
              data={mappedData}
              onClick={onClick}
              shape={<ScatterShape selectedVehicleId={props.selectedVehicleId} />}
            />
            <YAxis
              dataKey="y"
              type="number"
              tickFormatter={yAxisTickFormatter}
              tickLine={false}
              fontSize={12}
              axisLine={false}
              width={getValueByDevice(device, 48, 72, 72, 72)}
              padding={{top: 40}}
              domain={axisDomain}
            >
              <Label
                value={i18n.t('entity.priceMap.labels.priceInThousands', {
                  currencySymbol: formatCurrencySymbol(currencies, currencyCode),
                })}
                angle={-90}
                position="insideLeft"
                fontSize={14}
              />
            </YAxis>
            <XAxis
              dataKey="x"
              type="number"
              axisLine={false}
              tickFormatter={xAxisTickFormatter}
              tickLine={false}
              fontSize={12}
              height={48}
              domain={axisDomain}
              padding={{right: 40}}
            >
              <Label
                value={i18n.t('entity.vehicle.labels.mileage')}
                position="insideBottom"
                fontSize={14}
              />
            </XAxis>
            {device !== 'mobile' && (
              <Tooltip
                content={
                  <ChartTooltip
                    mySourcingVehicle={props.vehicle.sourcingVehicle}
                    data-testid={suffixTestId('chartTooltip', props)}
                  />
                }
                wrapperStyle={{outline: 'none'}}
                cursor={false}
              />
            )}
            {isNotNil(myVehicleItem?.x) && isNotNil(myVehicleItem?.y) && (
              <>
                {renderReferenceLineX({
                  value: myVehicleItem.x,
                  formatter: xAxisTickFormatter,
                  flagColor: 'black',
                  flagVerticalAlign: 'bottom',
                  flagHorizontalAlign: 'left',
                  lineColor: MY_VEHICLE_COLOR,
                  isLabelVisible: true,
                })}
                {renderReferenceLineY({
                  value: myVehicleItem.y,
                  formatter: yReferenceLineLabelFormatter,
                  flagColor: 'black',
                  flagVerticalAlign: 'bottom',
                  lineColor: MY_VEHICLE_COLOR,
                })}
              </>
            )}
            {length(mappedData) > 1 && (
              <>
                {renderReferenceLineY({
                  value: averageValueY,
                  formatter: (value) => `Ø ${yReferenceLineLabelFormatter(value)}`,
                  lineColor: theme.colors.palettes.neutral[60][100],
                  flagColor: 'neutral',
                  flagVerticalAlign: 'bottom',
                  flagHorizontalAlign: 'right',
                  isFlagSubtle: true,
                  isDashed: true,
                })}
                {isNotNil(averageValueX) &&
                  renderReferenceLineX({
                    value: averageValueX,
                    formatter: (value) => `Ø ${xAxisTickFormatter(value)}`,
                    lineColor: theme.colors.palettes.neutral[60][100],
                    flagColor: 'neutral',
                    flagHorizontalAlign: 'right',
                    isFlagSubtle: true,
                    isDashed: true,
                    isLabelVisible: true,
                  })}
              </>
            )}
          </ScatterChart>
        )}
      </AutoSizer>
    </Box>
  );
}
